import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import { readStoredAuthUser, normalizeTenantUser, resolveUserShopId } from '../utils/adminShopContext';
import { clearAiStudioSessions } from '../utils/haircutsStudioSession';

export const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => readStoredAuthUser());
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Resolve tenant context (shop) without hardcoding defaults.
  // Priority:
  // 1) Logged-in user payload (most reliable)
  // 2) URL query param (supports superadmin impersonation flows)
  const shopId = resolveUserShopId(user);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token');
      }
      const storedUser = readStoredAuthUser();
      const response = await axiosClient.get('/auth/me');

      if (response.data.ok) {
        const normalizedUser = normalizeTenantUser(response.data.user, storedUser);
        setUser(normalizedUser);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        setPermissions(response.data.permissions);
      } else {
        throw new Error('Failed to fetch user data');
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      localStorage.removeItem('user');
      setError(err.message);
      setUser(null);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearAiStudioSessions();
    setUser(null);
    setPermissions(null);
    setError(null);
  };

  const hasPermission = (permission) => {
    if (!permissions) return false;
    
    // Special handling for inventory permission - only for shop admins and superadmins
    if (permission === 'inventory') {
      return user && ['superadmin', 'shop_admin'].includes(user.role);
    }
    
    return permissions.canView.includes(permission);
  };

  const canEdit = (permission) => {
    if (!permissions) return false;
    return permissions.canEdit.includes(permission);
  };

  const hasAdminAccess = () => {
    if (!user) return false;
    return ['superadmin', 'shop_admin', 'barber'].includes(user.role);
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    const handleProfileImageUpdated = (event) => {
      const { userId, avatar_url } = event.detail || {};
      if (!userId || Number(user?.id) !== Number(userId)) return;

      const updatedUser = { ...user, avatar_url: avatar_url || null };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    };

    window.addEventListener('profile-image-updated', handleProfileImageUpdated);
    return () => window.removeEventListener('profile-image-updated', handleProfileImageUpdated);
  }, [user]);

  const value = {
    user,
    shopId,
    permissions,
    loading,
    error,
    fetchUserData,
    logout,
    hasPermission,
    canEdit,
    hasAdminAccess,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
